import React, { useState } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import {
  Container,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material'
import {
  useReasoningTraces,
  useConversations,
  downloadTraceFile,
} from '@/services/reasoning-traces/reasoning-traces.service'
import { getCurrentSymphonyUserId } from '@/services/reasoning-traces/api/reasoning-traces'
import { ReasoningTraceFilters } from '@/services/reasoning-traces/types'
import { useAppSelector } from '@/app/hooks'
import storage from '@/utils/storage'
import Header from '../header'
import TableComponent from '@/components/table'
import { HeadCell } from '@/components/table/auxiliar/types'
import Button from '@/components/ui/button'
import Input from '@/components/ui/input'
import { Pagination } from '@/components/pagination'
import { formatDistanceToNow } from '@/utils/format'
import {
  UserInfoSection,
  FiltersSection,
  StatusBadge,
  LoadingWrapper,
  EmptyState,
  ErrorState,
  TableWrapper,
  ActionsCell,
} from './styled'

const ReasoningTraces: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [page, setPage] = useState(1)
  const [filters, setFilters] = useState<ReasoningTraceFilters>({})
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedStatus, setSelectedStatus] = useState<
    'running' | 'completed' | 'error' | undefined
  >()
  const [selectedConversation, setSelectedConversation] = useState<
    string | undefined
  >()

  // Get current user information from Redux
  const userEmail = useAppSelector((state) => state.authUser.userMail)
  const symphonyUserId = useAppSelector(
    (state) => state.authUser.symphonyUserId,
  )
  const regularUserId = useAppSelector((state) => state.authUser.userId)

  // Get the actual Symphony ID being used for API calls
  const actualSymphonyId = getCurrentSymphonyUserId()

  // Determine if we're using test data
  const isUsingTestData =
    actualSymphonyId === 'test_user_001' || actualSymphonyId === 'test_user_002'
  const isUsingSymphonyId =
    symphonyUserId && actualSymphonyId === symphonyUserId

  // Check if we're on a detail page
  const isDetailPage = location.pathname.includes('/reasoning-traces/session_')

  // Fetch conversations for the dropdown
  const { data: conversationsData } = useConversations()

  const { data, isLoading, error } = useReasoningTraces({
    page,
    limit: 10,
    filters,
  })

  const handleSearch = () => {
    setFilters({
      search: searchTerm,
      status: selectedStatus,
      conversationId: selectedConversation,
    })
    setPage(1)
  }

  const handleConversationChange = (conversationId?: string) => {
    setSelectedConversation(conversationId)
    setFilters({
      ...filters,
      conversationId,
    })
    setPage(1)
  }

  const handleStatusFilter = (status?: 'running' | 'completed' | 'error') => {
    setSelectedStatus(status)
  }

  const handleTraceClick = (sessionId: string) => {
    navigate(`/reasoning-traces/${sessionId}`)
  }

  const handleDownloadAll = async (trace: any, e: React.MouseEvent) => {
    e.stopPropagation()
    // Note: For the list view, files array is empty until we fetch full details
    // This functionality would need to fetch full trace details first
    console.warn(
      'Download all functionality requires fetching full trace details first',
    )
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return '#4BA765'
      case 'running':
        return '#FFA500'
      case 'error':
        return '#FF4444'
      default:
        return '#666666'
    }
  }

  const formatDuration = (seconds?: number) => {
    if (!seconds) return '-'
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = Math.floor(seconds % 60)
    if (hours > 0) {
      return `${hours}h ${minutes}m`
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`
    }
    return `${secs}s`
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ padding: '30px' }}>
        <ErrorState>
          <h3>Error loading reasoning traces</h3>
          <p>{error instanceof Error ? error.message : 'An error occurred'}</p>
          <Button onClick={() => window.location.reload()}>Retry</Button>
        </ErrorState>
      </Container>
    )
  }

  // If we're on a detail page, render the outlet
  if (isDetailPage) {
    return <Outlet />
  }

  // Table headers configuration - add conversation column when not filtering by specific conversation
  const baseHeaders: HeadCell[] = [
    {
      id: 'started_at',
      numeric: false,
      disablePadding: false,
      label: 'Started',
    },
    {
      id: 'title',
      numeric: false,
      disablePadding: false,
      label: 'Title',
      maxCharactersWidth: 50,
    },
  ]

  // Only show conversation column when viewing all conversations
  if (!selectedConversation) {
    baseHeaders.push({
      id: 'conversation',
      numeric: false,
      disablePadding: false,
      label: 'Conversation',
    })
  }

  const headers: HeadCell[] = [
    ...baseHeaders,
    {
      id: 'status',
      numeric: false,
      disablePadding: false,
      label: 'Status',
    },
    {
      id: 'duration',
      numeric: false,
      disablePadding: false,
      label: 'Duration',
    },
    {
      id: 'phases',
      numeric: false,
      disablePadding: false,
      label: 'Phases',
    },
    {
      id: 'files',
      numeric: true,
      disablePadding: false,
      label: 'Files',
    },
    {
      id: 'outputs',
      numeric: true,
      disablePadding: false,
      label: 'Outputs',
    },
    {
      id: 'actions',
      numeric: false,
      disablePadding: false,
      label: 'Actions',
      notSortable: true,
    },
  ]

  // Transform data for table
  const tableData =
    data?.traces.map((trace, index) => {
      const baseRow: any = {
        started_at: new Date(trace.started_at).toLocaleString(),
        title: (
          <span
            style={{ cursor: 'pointer', color: '#1976d2' }}
            onClick={() => handleTraceClick(trace.session_id)}
          >
            {trace.title}
          </span>
        ),
      }

      // Add conversation info if not filtering by specific conversation
      if (!selectedConversation) {
        // Find the conversation this trace belongs to
        const conversation = conversationsData?.conversations.find(
          (conv) => conv.conversation_id === trace.conversation_id,
        )

        baseRow.conversation = conversation ? (
          <span style={{ display: 'flex', alignItems: 'center' }}>
            {conversation.type === 'GROUP' ? '👥 ' : '💬 '}
            {conversation.name}
          </span>
        ) : (
          <span style={{ color: '#666' }}>Unknown</span>
        )
      }

      return {
        ...baseRow,
        status: (
          <StatusBadge color={getStatusColor(trace.status)}>
            {trace.status}
          </StatusBadge>
        ),
        duration: formatDuration(trace.duration),
        phases: `${trace.summary?.completed_phases || 0}/${
          trace.summary?.total_phases || 0
        }`,
        files: trace.summary?.total_files || 0,
        outputs: trace.summary?.total_outputs || 0,
        actions: (
          <ActionsCell>
            <Button
              variant="secondary"
              onClick={() => handleTraceClick(trace.session_id)}
            >
              View
            </Button>
            {trace.files.length > 0 && (
              <Button
                variant="secondary"
                onClick={(e) => handleDownloadAll(trace, e)}
                style={{ marginLeft: '8px' }}
              >
                Download
              </Button>
            )}
          </ActionsCell>
        ),
      }
    }) || []

  return (
    <Container maxWidth="xl" sx={{ padding: '30px' }}>
      <Header
        title="AI Reasoning Traces"
        description="View and analyze AI reasoning sessions and their outputs"
      />

      <UserInfoSection>
        <div className="info-item">
          <strong>User:</strong>
          <span>{userEmail || 'Not logged in'}</span>
        </div>
        <div className="info-item">
          <strong>Symphony ID:</strong>
          <span>{actualSymphonyId}</span>
          {!isUsingSymphonyId && symphonyUserId && (
            <span
              style={{ color: '#856404', fontSize: '12px', marginLeft: '8px' }}
            >
              (Expected: {symphonyUserId})
            </span>
          )}
        </div>
        {!symphonyUserId && regularUserId && (
          <div
            className="info-item"
            style={{ fontSize: '12px', color: '#666' }}
          >
            <strong>Note:</strong>
            <span>No Symphony ID found, using user ID as fallback</span>
          </div>
        )}
        {isUsingTestData ? (
          <div className="test-badge">Using Test Data</div>
        ) : (
          <div className="live-badge">Live Symphony Data</div>
        )}
      </UserInfoSection>

      <FiltersSection>
        <div className="filters-row">
          <div className="filter-buttons">
            <Button
              variant={!selectedStatus ? 'primary' : 'secondary'}
              onClick={() => handleStatusFilter(undefined)}
            >
              All
            </Button>
            <Button
              variant={selectedStatus === 'completed' ? 'primary' : 'secondary'}
              onClick={() => handleStatusFilter('completed')}
            >
              Completed
            </Button>
            <Button
              variant={selectedStatus === 'running' ? 'primary' : 'secondary'}
              onClick={() => handleStatusFilter('running')}
            >
              Running
            </Button>
            <Button
              variant={selectedStatus === 'error' ? 'primary' : 'secondary'}
              onClick={() => handleStatusFilter('error')}
            >
              Failed
            </Button>
          </div>

          <FormControl style={{ minWidth: 200, marginLeft: '16px' }}>
            <InputLabel id="conversation-select-label">Conversation</InputLabel>
            <Select
              labelId="conversation-select-label"
              value={selectedConversation || ''}
              onChange={(e) =>
                handleConversationChange(e.target.value || undefined)
              }
              label="Conversation"
            >
              <MenuItem value="">
                <em>All Conversations</em>
              </MenuItem>
              {conversationsData?.conversations.map((conv) => (
                <MenuItem
                  key={conv.conversation_id}
                  value={conv.conversation_id}
                >
                  {conv.type === 'GROUP' ? '👥 ' : '💬 '}
                  {conv.name}
                  {conv.trace_count > 0 && ` (${conv.trace_count})`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <div className="search-section">
            <Input
              placeholder="Search by title or session ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
            <Button onClick={handleSearch} width="auto" height="54px">
              Search
            </Button>
          </div>
        </div>
      </FiltersSection>

      {isLoading ? (
        <LoadingWrapper>
          <p>Loading reasoning traces...</p>
        </LoadingWrapper>
      ) : data?.traces.length === 0 ? (
        <EmptyState>
          <h3>No reasoning traces found</h3>
          <p>Try adjusting your filters or search criteria</p>
        </EmptyState>
      ) : (
        <TableWrapper>
          <TableComponent headers={headers} rows={tableData} />

          {data && data.total > data.limit && (
            <Pagination
              currentPage={page}
              totalPages={Math.ceil(data.total / data.limit)}
              onPageChange={setPage}
            />
          )}
        </TableWrapper>
      )}
    </Container>
  )
}

export default ReasoningTraces
